import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button, Chip, Typography } from '@mui/material';
import { AppPage, AppSection, FigureRow } from '../../components/app/AppPage';
import { billingService, errorMessage, resetPlanCache, Plan, PlanId, PlansResponse, Subscription } from '../../services/billingService';
import { logger } from '../../utils/logger';

const money = (cents: number) => `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null);
const RANK: Record<PlanId, number> = { monitor: 0, standard: 1, pro: 2 };

type Notice = { severity: 'success' | 'info' | 'warning' | 'error'; text: string } | null;

const statusLine = (sub: Subscription | null): string | null => {
  if (!sub || sub.plan === 'monitor') return null;
  const end = day(sub.current_period_end);
  if (sub.status === 'past_due') return 'Your last payment did not go through. Update your card to keep the plan.';
  if (sub.cancel_at_period_end) return end ? `Ends on ${end}. You can renew it from Manage billing.` : 'Set to end at the close of this period.';
  return end ? `Renews on ${end}.` : 'Active.';
};

const BillingPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<PlansResponse | null>(null);
  const [sub, setSub] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice>(null);

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([billingService.plans(), billingService.subscription()]);
      setPlans(p);
      setSub(s);
      resetPlanCache();
    } catch (err) {
      logger.error('[Billing] load failed', err);
      setNotice({ severity: 'error', text: errorMessage(err, 'We could not load your plan. Refresh to try again.') });
    } finally {
      setLoading(false);
    }
  }, []);

  // Coming back from Stripe Checkout: record the result, then tidy the URL.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const outcome = params.get('checkout');
    const sessionId = params.get('session_id');
    if (!outcome) { load(); return; }
    (async () => {
      if (outcome === 'success' && sessionId) {
        try {
          const s = await billingService.confirm(sessionId);
          setNotice(s.plan === 'monitor'
            ? { severity: 'info', text: 'Payment received. Your plan will update in a moment; refresh if it does not.' }
            : { severity: 'success', text: `You are on ${s.plan === 'pro' ? 'Pro' : 'Standard'}.` });
        } catch (err) {
          setNotice({ severity: 'warning', text: errorMessage(err, 'Checkout finished, but we could not confirm it yet. Refresh in a minute.') });
        }
      } else if (outcome === 'cancelled') {
        setNotice({ severity: 'info', text: 'Checkout cancelled. Nothing was charged.' });
      }
      navigate('/billing', { replace: true });
      await load();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upgrade = async (plan: Exclude<PlanId, 'monitor'>) => {
    setBusy(plan);
    setNotice(null);
    try {
      window.location.assign(await billingService.checkout(plan));
    } catch (err) {
      setNotice({ severity: 'error', text: errorMessage(err, 'Could not open checkout. Please try again.') });
      setBusy(null);
    }
  };

  const manage = async () => {
    setBusy('portal');
    setNotice(null);
    try {
      window.location.assign(await billingService.portal());
    } catch (err) {
      setNotice({ severity: 'error', text: errorMessage(err, 'Could not open billing. Please try again.') });
      setBusy(null);
    }
  };

  const current: PlanId = sub?.plan ?? 'monitor';
  const currentPlan = useMemo(() => plans?.plans.find((p) => p.id === current), [plans, current]);
  const paid = current !== 'monitor';
  const canBuy = Boolean(plans?.checkout_available);

  const action = (plan: Plan) => {
    if (plan.id === current) {
      return paid
        ? <Button variant="outlined" onClick={manage} disabled={busy !== null}>{busy === 'portal' ? 'Opening…' : 'Manage billing'}</Button>
        : <Typography variant="body2" color="text.secondary">Your current plan</Typography>;
    }
    if (plan.id === 'monitor') {
      return paid ? <Button variant="text" onClick={manage} disabled={busy !== null}>Return to Monitor</Button> : null;
    }
    if (paid) {
      // Changing between paid plans happens in the Stripe portal, with proration.
      return <Button variant={RANK[plan.id] > RANK[current] ? 'contained' : 'text'} onClick={manage} disabled={busy !== null}>
        {RANK[plan.id] > RANK[current] ? `Move to ${plan.name}` : `Switch to ${plan.name}`}
      </Button>;
    }
    if (!canBuy) {
      return <Button variant={plan.id === 'standard' ? 'contained' : 'outlined'} component={RouterLink} to="/contact">Talk to sales</Button>;
    }
    return (
      <Button variant={plan.id === 'standard' ? 'contained' : 'outlined'} onClick={() => upgrade(plan.id as 'standard' | 'pro')} disabled={busy !== null}>
        {busy === plan.id ? 'Opening checkout…' : `Upgrade to ${plan.name}`}
      </Button>
    );
  };

  return (
    <AppPage
      title="Plan and billing"
      description="Your GoTruTax plan, what each plan includes, and where to change it."
      actions={paid ? <Button variant="outlined" onClick={manage} disabled={busy !== null}>{busy === 'portal' ? 'Opening…' : 'Manage billing'}</Button> : undefined}
    >
      {notice && <Alert severity={notice.severity} sx={{ mb: 3 }} onClose={() => setNotice(null)}>{notice.text}</Alert>}
      {plans?.mode === 'test' && (
        <Alert severity="info" variant="outlined" sx={{ mb: 3 }}>
          Billing is in test mode. Checkout uses Stripe test cards and no money moves.
        </Alert>
      )}

      {loading ? (
        <div className="gt-skeleton" style={{ height: 240 }} aria-busy="true" aria-label="Loading plans" />
      ) : (
        <>
          <FigureRow
            items={[
              { label: 'Current plan', value: currentPlan?.name ?? 'Monitor', note: statusLine(sub) ?? 'Free, no card on file.' },
              { label: 'Price', value: currentPlan && currentPlan.amount_cents ? `${money(currentPlan.amount_cents)}/mo` : '$0', note: paid ? 'Billed monthly by Stripe.' : 'Upgrade any time; cancel any time.' },
            ]}
          />
          <AppSection
            title="Plans"
            description={canBuy || paid
              ? 'Upgrades are monthly and billed through Stripe. Change or cancel from Manage billing.'
              : 'Online checkout is not open for your account yet. Talk to sales and we will set up your plan.'}
            flat
          >
            <div className="gt-plans">
              {(plans?.plans ?? []).map((plan) => (
                <div key={plan.id} className="gt-plan-col" data-current={plan.id === current}>
                  <Typography variant="h6" component="h3" className="gt-plan-name">
                    {plan.name}
                    {plan.id === current && <Chip size="small" color="primary" label="Current" />}
                  </Typography>
                  <div className="gt-plan-price">
                    {plan.amount_cents ? <>{money(plan.amount_cents)}<small> /month</small></> : <>Free</>}
                  </div>
                  <ul className="gt-plan-list">
                    {plan.highlights.map((h) => <li key={h}>{h}</li>)}
                  </ul>
                  <div className="gt-plan-cta">{action(plan)}</div>
                </div>
              ))}
            </div>
          </AppSection>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            Questions about a plan or an invoice? <RouterLink to="/contact">Contact us</RouterLink>.
          </Typography>
        </>
      )}
    </AppPage>
  );
};

export default BillingPage;
